import React, { useCallback, useEffect, useRef, useState } from 'react';
import { WishlistOutlined } from 'jl-design-system/components/icons/WishlistOutlined';
import { AddToWishListNotification } from './AddToWishListNotification';
import { isMin1280pxWide } from '../../utils/device/identifyDevice';

const popupDisplayDurationMs = 10000;

interface AccountWishListProps {
  wishListUrl: string;
  signInUrl: string;
  registerUrl: string;
  signedIn: boolean;
  lgOnlyClassname?: string;
}

export const AccountWishList: React.FC<AccountWishListProps> = ({
  wishListUrl,
  signInUrl,
  registerUrl,
  signedIn,
  lgOnlyClassname,
}) => {
  const [isNotificationDisplayed, setIsNotificationDisplayed] = useState(false);

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLLIElement>(null);

  const startCloseTimer = useCallback(() => {
    // Clear any existing timer
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Start new timer
    timeoutRef.current = setTimeout(() => {
      setIsNotificationDisplayed(false);
    }, popupDisplayDurationMs);
  }, []);

  const handleWishListEvent = useCallback(() => {
    if (isMin1280pxWide()) {
      setIsNotificationDisplayed(true);
      startCloseTimer();
    }
  }, [startCloseTimer]);

  const handleClosePopup = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsNotificationDisplayed(false);
  };

  useEffect(
    function listenForGuestWishlistSuccess() {
      window.addEventListener(
        'ADD_TO_WISHLIST_ESI:GUEST_SUCCESS',
        handleWishListEvent
      );

      return function stopListeningForGuestWishlistSuccess() {
        window.removeEventListener(
          'ADD_TO_WISHLIST_ESI:GUEST_SUCCESS',
          handleWishListEvent
        );
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    },
    [handleWishListEvent]
  );

  return (
    <li ref={containerRef} className={lgOnlyClassname}>
      <a
        href={wishListUrl}
        data-test="wishlist-button"
        data-testid="wishlist-button"
        aria-label="View your wishlist"
      >
        <WishlistOutlined size="large" />
      </a>
      {!signedIn && isNotificationDisplayed && (
        <AddToWishListNotification
          signInUrl={signInUrl}
          registerUrl={registerUrl}
          onClose={handleClosePopup}
        />
      )}
    </li>
  );
};
